/**
 * Step 1/4 — tone selection per AI-09 §C1. Four presets from the spec
 * mockup map onto single enum tones; "Custom" stores tone=null +
 * custom_tone free text (both valid server-side).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  VOICE_LANGUAGE_LEVELS,
  type BrandKitVoice,
  type VoiceTone,
} from "../../../../lib/brandKits";

const TONE_PRESETS: VoiceTone[] = ["formal", "friendly", "playful", "professional"];

export interface StepProps {
  draft: BrandKitVoice;
  update: (patch: Partial<BrandKitVoice>) => void;
}

export function ToneStep({ draft, update }: StepProps): JSX.Element {
  const { t } = useTranslation();
  const [customSelected, setCustomSelected] = useState(
    () => (draft.tone ?? null) === null && (draft.custom_tone ?? "") !== "",
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">{t("brandVoice.tone.question")}</p>
      <div role="radiogroup" aria-label={t("brandVoice.tone.question")} className="space-y-2">
        {TONE_PRESETS.map((tone) => {
          const selected = !customSelected && draft.tone === tone;
          return (
            <label
              key={tone}
              className={
                "flex cursor-pointer items-start gap-3 rounded-md border p-3 " +
                (selected ? "border-primary-500 bg-primary-50" : "border-neutral-200 hover:bg-neutral-50")
              }
            >
              <input
                type="radio"
                name="voice-tone"
                className="mt-1"
                checked={selected}
                onChange={() => {
                  setCustomSelected(false);
                  update({ tone, custom_tone: null });
                }}
              />
              <span>
                <span className="block text-sm font-medium text-neutral-900">
                  {t(`brandVoice.tones.${tone}.label`)}
                </span>
                <span className="block text-xs text-neutral-500">
                  {t(`brandVoice.tones.${tone}.description`)}
                </span>
                <span className="mt-0.5 block text-xs italic text-neutral-400">
                  {t(`brandVoice.tones.${tone}.example`)}
                </span>
              </span>
            </label>
          );
        })}
        <label
          className={
            "flex cursor-pointer items-start gap-3 rounded-md border p-3 " +
            (customSelected ? "border-primary-500 bg-primary-50" : "border-neutral-200 hover:bg-neutral-50")
          }
        >
          <input
            type="radio"
            name="voice-tone"
            className="mt-1"
            checked={customSelected}
            onChange={() => {
              setCustomSelected(true);
              update({ tone: null });
            }}
          />
          <span className="flex-1">
            <span className="block text-sm font-medium text-neutral-900">
              {t("brandVoice.tone.custom_label")}
            </span>
            {customSelected && (
              <textarea
                aria-label={t("brandVoice.tone.custom_label")}
                value={draft.custom_tone ?? ""}
                maxLength={200}
                rows={2}
                placeholder={t("brandVoice.tone.custom_placeholder")}
                onChange={(e) => update({ custom_tone: e.target.value })}
                className="mt-2 block w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            )}
          </span>
        </label>
      </div>

      <div>
        <label htmlFor="voice-language-level" className="block text-sm font-medium text-neutral-700">
          {t("brandVoice.tone.language_level")}
        </label>
        <select
          id="voice-language-level"
          value={draft.language_level ?? "conversational"}
          onChange={(e) =>
            update({ language_level: e.target.value as BrandKitVoice["language_level"] })
          }
          className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {VOICE_LANGUAGE_LEVELS.map((level) => (
            <option key={level} value={level}>
              {t(`brandVoice.language_levels.${level}`)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
